<template>
    <Popup :open="open" @open="toggleOpen" popupWidth="w-3/4 md:w-3/4 lg:w-1/3" :padding="true">
        <Loading :loading="loading" class="min-h-full">
            <div v-if="!loading">
                <div class="py-4 border-b border-gray-300 w-full">
                    <strong>
                        {{ $t('subscription_extend') }}
                    </strong>
                </div>

                <div class="mt-4">
                    <div class="py-4">
                        <div class="flex flex-row items-center gap-4">
                            <div class="w-[50px] h-[50px] rounded-md text-white">
                                <img
                                    :alt="$translate(abonnement.product.name)"
                                    class="w-full h-full object-contain"
                                    :src="abonnement.product.image.replace('.png', '-gradient.png')"
                                />
                            </div>
                            <strong>
                                {{ $translate(abonnement.product.name) }}
                            </strong>
                        </div>
                        <div class="mt-4">
                            <p>
                                {{ $t('extend_your_annual_subscription') }}
                            </p>
                            <strong v-if="abonnement.discountCouponCodeName != null"><br>{{ $t('replace_abo_coupon') }} <br></strong>
                            <strong v-if="abonnement.recurring == 0"><br>{{ $t('reactivate_abo') }}<br></strong>
                            <br />
                            <strong>
                                {{ $t('enter_discount_code') }}
                            </strong>
                            <input
                                v-model="discountCode"
                                id="discountCode"
                                type="text"
                                class="border border-gray-300 rounded-md w-full px-4 py-2 mt-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                :placeholder="$t('personal_discount_code')"
                            />
                        </div>
                    </div>

                    <hr class="w-full border-gray-300 border-b-0.5 my-4" />

                    <FormButton
                        @click="openConfirmDialog"
                        class="mt-3"
                        styling="primary flex items-center gap-3 w-full justify-center"
                        :value="$t('extend_12_months')"
                        :disabled="isProcessing"
                    />
                </div>
            </div>
        </Loading>
    </Popup>

    <Popup v-if="confirmDialogOpen" :open="confirmDialogOpen" @open="confirmDialogOpen = !confirmDialogOpen" popupWidth="w-3/4 md:w-3/4 lg:w-1/3">
        <div class="p-6">
            <h2 class="text-lg font-bold mb-4">{{ $t('confirm_extension') }}</h2>
            <p class="text-sm text-gray-700">
                {{ $t('extend_your_abonnement_for_another_12_months') }}<br />
                <b
                    >{{ $t('discount_code') }} <span class="font-bold">{{ discountCode ? discountCode : '-' }}</span></b
                ><br />
                <b
                    >{{ $t('plain_discount') }}: <span class="font-bold">{{ discountText ? discountText : '-' }}</span></b
                >
            </p>

            <div class="my-4">
                <Checkbox name="conditions" :label="conditionsText" v-model:value="acceptedConditions" class="m-0 my-2 text-xs" />
                <div v-if="conditionsNotAcceptedError" class="mt-2 text-red text-xs">
                    <p>
                        {{ $t('accept_terms_and_conditions') }}
                    </p>
                </div>
            </div>
            <div class="my-4">
                <Checkbox name="terms_and_conditions" :label="termsText" v-model:value="acceptedTermsConditions" class="m-0 my-2 text-xs" />
                <div v-if="termsNotAcceptedError" class="mt-2 text-red text-xs">
                    <p>
                        {{ $t('accept_conditions') }}
                    </p>
                </div>
            </div>

            <div class="flex justify-end gap-4 mt-6">
                <button
                    @click="confirmDialogOpen = false"
                    class="px-6 py-3 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-all duration-200 shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                    {{ $t('common_cancel') }}
                </button>

                <button
                    @click="confirmExtension"
                    class="w-auto px-6 py-3 flex items-center gap-3 justify-center rounded-md border text-white bg-[#031837] border-white transition-all duration-200 shadow-md hover:bg-[#02132a] focus:outline-none focus:ring-2 focus:ring-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                    :disabled="isProcessing"
                >
                    {{ $t('common_confirm') }}
                </button>
            </div>
        </div>
    </Popup>
</template>

<script>
import Toastification from '../miscellaneous/Toastification.vue';
import api from '../../api.js';
import i18next from 'i18next';

export default {
    props: {
        open: {
            type: Boolean,
            required: true
        },
        abonnement: {
            type: Object,
            required: true
        }
    },
    emits: ['update:open', 'reload'],
    data() {
        return {
            discountCode: '',
            discountText: '',
            loading: false,
            isProcessing: false,
            confirmDialogOpen: false,
            termsText: i18next.t('terms_conditions_consent'),
            conditionsText: i18next.t('contract_execution_before_cancellation'),
            acceptedTermsConditions: false,
            acceptedConditions: false,
            termsNotAcceptedError: false,
            conditionsNotAcceptedError: false
        };
    },
    methods: {
        toggleOpen() {
            this.$emit('update:open', !this.open);
        },
        async openConfirmDialog() {
            if (!this.discountCode) {
                this.$toast.error({
                    component: Toastification,
                    props: {
                        message: this.$t('enter_discount_code')
                    }
                });
                return;
            }

            this.loading = true;

            try {
                const response = await api.get(`/api/v1/coupons/validate/${this.discountCode}`);

                if (response.data.valid) {
                    this.confirmDialogOpen = true;
                    this.discountText = response.data.text;
                } else {
                    this.$toast.error({
                        component: Toastification,
                        props: {
                            message: this.$t('coupon_code_invalid')
                        }
                    });
                }
            } catch (error) {
                this.$toast.error({
                    component: Toastification,
                    props: {
                        message: this.$t('discount_code_validation_error')
                    }
                });
            } finally {
                this.loading = false;
            }
        },
        async confirmExtension() {
            if (this.isProcessing) return;

            if (this.acceptedTermsConditions === false || this.acceptedConditions === false) {
                if (this.acceptedTermsConditions === false) {
                    this.termsNotAcceptedError = true;
                }

                if (this.acceptedConditions === false) {
                    this.conditionsNotAcceptedError = true;
                }

                this.$scrollToId('#terms');
                this.loading = false;
                return;
            }

            this.isProcessing = true;
            this.loading = true;
            this.confirmDialogOpen = false;

            const updatedAbonnement = {
                ...this.abonnement,
                renewal_discount_code: this.discountCode
            };

            const payload = {
                abonnementId: this.abonnement.id,
                renewal_discount_code: this.discountCode
            };

            try {
                const response = await this.$store.dispatch('abonnements/update', {
                    id: this.abonnement.id,
                    data: updatedAbonnement
                });

                this.abonnement.renewal_discount_code = response.renewal_discount_code;

                this.$toast.success({
                    component: Toastification,
                    props: {
                        message: this.$t('abonnement_renewal_success')
                    }
                });
                this.toggleOpen();
                this.$emit('reload');
            } catch (error) {
                this.$toast.error({
                    component: Toastification,
                    props: {
                        message: this.$t('abonnement_renewal_error')
                    }
                });
                this.$emit('reload');
            } finally {
                this.isProcessing = false;
                this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
/* Optional Styling for Better Visual Clarity */
#discountCode {
    font-size: 1rem;
    color: #4a5568; /* Dark Gray Text */
}

#discountCode::placeholder {
    color: #a0aec0; /* Light Gray Placeholder */
    opacity: 1; /* Ensure placeholder is visible */
}

.focus\:ring-2:focus {
    box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.6); /* Blue focus ring */
}
</style>
